/**
 * Urban Brain Health Model — Literature-derived parameters.
 *
 * Adapted from the building-scale Brain Health Model for urban/site scale.
 * Same 8 pathways, but measured via urban indicators and indices.
 */

// ── Pathway Weights (for aggregation — sum of positive = 1.0, negative weights applied separately) ──

export const PATHWAY_WEIGHTS = {
  movement: 0.15,
  nature: 0.18,
  daylight: 0.12,
  air_quality: 0.18,
  noise: 0.12,
  enrichment: 0.12,
  wayfinding: 0.06,
  visual: 0.07,
};

// ── Movement / Walkability ──

export const MOVEMENT = {
  walkScore_optimal: 90,
  walkScore_good: 70,
  walkScore_min: 50,
  bikeInfra_optimal: 0.8,
  blockPermeability_optimal: 0.7,
  synergy_nature_movement_bonus: 0.15,
  synergy_mixeduse_walkability_bonus: 0.10,
};

// ── Nature / Green Infrastructure ──

export const NATURE = {
  treeCanopy_optimal_pct: 40,
  treeCanopy_good_pct: 25,
  treeCanopy_min_pct: 10,
  greenSpaceAccess_optimal_pct: 90,
  greenSpaceAccess_good_pct: 60,
  permeableSurface_optimal_pct: 50,
  permeableSurface_good_pct: 30,
  // Evidence: WHO recommends access to green space within 300m for all residents
  uhi_reduction_per_pct_canopy: 0.3, // °C per % canopy increase
  cortisol_reduction_per_hour_nature: 0.096, // from Hunter 2019
};

// ── Daylight / Solar Access ──

export const DAYLIGHT = {
  skyViewFactor_optimal: 0.6,
  skyViewFactor_good: 0.4,
  skyViewFactor_min: 0.2,
  sunlightHours_optimal: 6,
  sunlightHours_good: 4,
  sunlightHours_min: 2,
};

// ── Air Quality ──

export const AIR_QUALITY = {
  pm25_who_guideline: 5, // µg/m³ annual
  pm25_acceptable: 12,
  pm25_unhealthy: 35,
  trafficBuffer_optimal_pct: 90, // % of zone >100m from major roads
  trafficBuffer_good_pct: 70,
  greenBuffer_optimal: 0.7,
  // Evidence: Liu 2021, Song 2022
  dementia_hr_per_ug: 1.08,
  healthcare_cost_per_ug_per_capita: 150, // estimated annual cost
};

// ── Noise Environment ──

export const NOISE = {
  pctOver65dB_max: 30, // % of zone with >65 dB Lden acceptable
  pctOver65dB_concern: 50,
  pctQuiet_optimal: 40, // % of zone <50 dB
  pctQuiet_good: 20,
  soundQuality_optimal: 0.7,
  // Evidence: de Deus 2017, WHO Environmental Noise Guidelines
  lden_65_health_threshold: true,
};

// ── Enrichment / Activation ──

export const ENRICHMENT = {
  mixedUse_optimal: 0.7, // Shannon diversity index
  mixedUse_good: 0.4,
  groundPlaneActivation_optimal_pct: 70,
  groundPlaneActivation_good_pct: 40,
  socialInfra_optimal_per1k: 3.0,
  socialInfra_good_per1k: 1.5,
  // Evidence: Vega-Rosas 2024 — enrichment domains
  cognitive_domain_bdnf_r: 0.62,
  social_domain_depression_r: -0.86,
};

// ── Wayfinding / Legibility ──

export const WAYFINDING = {
  intelligibility_optimal: 0.7,
  intelligibility_good: 0.4,
  landmark_optimal: 0.7,
  landmark_good: 0.4,
};

// ── Visual Environment ──

export const VISUAL = {
  facadeDiversity_optimal: 0.7,
  facadeDiversity_good: 0.4,
  streetscapeInterest_optimal: 0.7,
  streetscapeInterest_good: 0.4,
  // Evidence: Valentine 2025 — visual stress at 3 cpd
};

// ── Synergy Parameters ──

export const SYNERGY = {
  nature_movement_threshold: 0.4,
  nature_movement_multiplier: 1.15,
  mixeduse_walkability_threshold: 0.4,
  mixeduse_walkability_multiplier: 1.10,
  triple_threshold: 0.5,
  triple_bonus: 0.08,
};

// ── Defaults (for missing/unspecified data) ──

export const DEFAULTS = {
  walkScore: 50,
  bikeInfrastructure: 0.3,
  blockPermeability: 0.4,
  treeCanopyPct: 15,
  greenSpaceAccess300m: 50,
  permeableSurfacePct: 20,
  skyViewFactor: 0.4,
  sunlightHoursGroundPlane: 4,
  pm25Annual: 12,
  pctZoneOver100mFromTraffic: 60,
  greenBufferScore: 0.3,
  pctZoneOver65dB: 30,
  pctQuietAreas: 20,
  soundEnvironmentQuality: 0.4,
  mixedUseIndex: 0.4,
  groundPlaneActivationPct: 40,
  socialInfraPerThousand: 1.5,
  streetIntelligibility: 0.5,
  landmarkScore: 0.5,
  facadeDiversityIndex: 0.5,
  streetscapeVisualInterest: 0.5,
  estimatedPopulation: 5000,
  areaAcres: 100,
};

// ── Regional Weights (dorsal = cognitive, ventral = emotional) ──

export const REGIONAL_WEIGHTS = {
  dorsal: {
    movement: 0.18,
    nature: 0.08,
    daylight: 0.12,
    enrichment: 0.45,
    wayfinding: 0.17,
  },
  ventral: {
    movement: 0.38,
    nature: 0.24,
    daylight: 0.18,
    enrichment: 0.14,
    wayfinding: 0.06,
  },
};
